import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentOrgId } from "@/hooks/useSkillOS";
import { useOrgRole } from "@/hooks/useOrgRole";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { RoleGate } from "@/components/RoleGate";
import NotFound from "@/pages/NotFound";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter,
} from "@/components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ShieldCheck, Plus, Search, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";

interface VerifiedEntityRow {
  id: string;
  org_id: string;
  group_id: string | null;
  entity_type: string;
  primary_name: string;
  aliases: string[];
  identifier_type: string | null;
  identifier_value: string | null;
  summary: string;
  profile: Record<string, unknown>;
  source_url: string | null;
  source_notes: string | null;
  verified_at: string;
  confidence: number;
  active: boolean;
  created_at: string;
  updated_at: string;
}

interface GroupRow { id: string; name: string }

const ENTITY_TYPES = [
  { value: "person", label: "Pessoa" },
  { value: "organization", label: "Organização" },
  { value: "product", label: "Produto" },
  { value: "location", label: "Local" },
  { value: "other", label: "Outro" },
];

interface FormState {
  id: string | null;
  entity_type: string;
  primary_name: string;
  aliases: string;
  identifier_type: string;
  identifier_value: string;
  summary: string;
  profile_json: string;
  source_url: string;
  source_notes: string;
  group_id: string;
  confidence: number;
  active: boolean;
}

const EMPTY_FORM: FormState = {
  id: null,
  entity_type: "person",
  primary_name: "",
  aliases: "",
  identifier_type: "",
  identifier_value: "",
  summary: "",
  profile_json: "{}",
  source_url: "",
  source_notes: "",
  group_id: "__org__",
  confidence: 1,
  active: true,
};

function CortexVerifiedEntitiesInner() {
  const qc = useQueryClient();
  const { data: orgId } = useCurrentOrgId();
  const { can } = useOrgRole();
  const canManage = can("cortex.entities.manage");

  const lookupOn = useFeatureFlag(orgId, "cortex.verified_entities.lookup");
  const enforceOn = useFeatureFlag(orgId, "cortex.verified_entities.enforce");
  const [flagBusy, setFlagBusy] = useState<string | null>(null);

  async function toggleFlag(flagKey: string, nextEnabled: boolean) {
    if (!orgId) return;
    setFlagBusy(flagKey);
    const { error } = await supabase
      .from("feature_flags")
      .upsert(
        {
          org_id: orgId,
          flag_key: flagKey,
          enabled: nextEnabled,
          rollout_pct: nextEnabled ? 100 : 0,
        },
        { onConflict: "org_id,flag_key" },
      );
    setFlagBusy(null);
    if (error) {
      toast.error("Erro ao atualizar flag: " + error.message);
      return;
    }
    toast.success(`${flagKey} ${nextEnabled ? "ativada" : "desativada"}`);
    qc.invalidateQueries({ queryKey: ["feature-flag", orgId, flagKey] });
  }

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [groupFilter, setGroupFilter] = useState<string>("all");
  const [showInactive, setShowInactive] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [confirmDelete, setConfirmDelete] = useState<VerifiedEntityRow | null>(null);

  const { data: entities = [], isLoading } = useQuery({
    queryKey: ["cortex-entities", orgId, showInactive],
    enabled: !!orgId,
    queryFn: async () => {
      let q = supabase
        .from("verified_entities")
        .select("*")
        .eq("org_id", orgId!)
        .order("verified_at", { ascending: false })
        .limit(200);
      if (!showInactive) q = q.eq("active", true);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as VerifiedEntityRow[];
    },
  });

  const { data: groups = [] } = useQuery({
    queryKey: ["cortex-entities-groups", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await supabase
        .from("syra_groups")
        .select("id, name")
        .eq("org_id", orgId!)
        .order("name");
      return (data ?? []) as GroupRow[];
    },
  });

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return entities.filter((e) => {
      if (typeFilter !== "all" && e.entity_type !== typeFilter) return false;
      if (groupFilter === "__org__" && e.group_id !== null) return false;
      if (groupFilter !== "all" && groupFilter !== "__org__" && e.group_id !== groupFilter) return false;
      if (term) {
        const hay = [
          e.primary_name,
          ...(e.aliases ?? []),
          e.identifier_value ?? "",
        ].join(" ").toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [entities, search, typeFilter, groupFilter]);

  function openCreate() {
    setForm(EMPTY_FORM);
    setDrawerOpen(true);
  }

  function openEdit(row: VerifiedEntityRow) {
    setForm({
      id: row.id,
      entity_type: row.entity_type,
      primary_name: row.primary_name,
      aliases: (row.aliases ?? []).join(", "),
      identifier_type: row.identifier_type ?? "",
      identifier_value: row.identifier_value ?? "",
      summary: row.summary,
      profile_json: JSON.stringify(row.profile ?? {}, null, 2),
      source_url: row.source_url ?? "",
      source_notes: row.source_notes ?? "",
      group_id: row.group_id ?? "__org__",
      confidence: Number(row.confidence ?? 1),
      active: row.active,
    });
    setDrawerOpen(true);
  }

  async function handleSave() {
    if (!orgId) return;
    if (!form.primary_name.trim() || !form.summary.trim()) {
      toast.error("Nome e resumo são obrigatórios");
      return;
    }
    let profile: Record<string, unknown> = {};
    try {
      profile = form.profile_json.trim() ? JSON.parse(form.profile_json) : {};
      if (typeof profile !== "object" || Array.isArray(profile)) throw new Error("must be object");
    } catch {
      toast.error("Profile precisa ser JSON válido (objeto)");
      return;
    }

    const aliases = form.aliases
      .split(",")
      .map((a) => a.trim())
      .filter(Boolean);

    const payload = {
      org_id: orgId,
      group_id: form.group_id === "__org__" ? null : form.group_id,
      entity_type: form.entity_type,
      primary_name: form.primary_name.trim(),
      aliases,
      identifier_type: form.identifier_type.trim() || null,
      identifier_value: form.identifier_value.trim() || null,
      summary: form.summary.trim(),
      profile: profile as any,
      source_url: form.source_url.trim() || null,
      source_notes: form.source_notes.trim() || null,
      confidence: form.confidence,
      active: form.active,
    };

    const op = form.id
      ? supabase.from("verified_entities").update(payload).eq("id", form.id)
      : supabase.from("verified_entities").insert(payload as any);

    const { error } = await op;
    if (error) {
      toast.error("Erro ao salvar: " + error.message);
      return;
    }
    toast.success(form.id ? "Entidade atualizada" : "Entidade cadastrada");
    setDrawerOpen(false);
    qc.invalidateQueries({ queryKey: ["cortex-entities", orgId] });
  }

  async function handleSoftDelete() {
    if (!confirmDelete) return;
    const { error } = await supabase
      .from("verified_entities")
      .update({ active: false })
      .eq("id", confirmDelete.id);
    if (error) {
      toast.error("Erro ao desativar: " + error.message);
      return;
    }
    toast.success("Entidade desativada");
    setConfirmDelete(null);
    qc.invalidateQueries({ queryKey: ["cortex-entities", orgId] });
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5" />
              Entidades verificadas
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Fonte autoritativa de fatos curados. Têm precedência absoluta sobre memória e treino do modelo.
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-3">
              <div className="flex items-center gap-2">
                <Switch
                  id="flag-lookup"
                  checked={lookupOn}
                  onCheckedChange={(v) => toggleFlag("cortex.verified_entities.lookup", v)}
                  disabled={!canManage || flagBusy === "cortex.verified_entities.lookup"}
                />
                <Label htmlFor="flag-lookup" className="text-sm cursor-pointer">
                  Lookup <span className="text-muted-foreground">({lookupOn ? "ON" : "OFF"})</span>
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="flag-enforce"
                  checked={enforceOn}
                  onCheckedChange={(v) => toggleFlag("cortex.verified_entities.enforce", v)}
                  disabled={!canManage || flagBusy === "cortex.verified_entities.enforce"}
                />
                <Label htmlFor="flag-enforce" className="text-sm cursor-pointer">
                  Enforce <span className="text-muted-foreground">({enforceOn ? "ON" : "OFF"})</span>
                </Label>
              </div>
            </div>
          </div>
          {canManage && (
            <Button onClick={openCreate} className="gap-2">
              <Plus className="w-4 h-4" /> Adicionar entidade
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2 top-2.5 w-4 h-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por nome, alias ou identificador"
                className="pl-8"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os tipos</SelectItem>
                {ENTITY_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={groupFilter} onValueChange={setGroupFilter}>
              <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos escopos</SelectItem>
                <SelectItem value="__org__">Org-wide</SelectItem>
                {groups.map((g) => (
                  <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <Switch
                id="show-inactive"
                checked={showInactive}
                onCheckedChange={setShowInactive}
              />
              <Label htmlFor="show-inactive" className="text-sm">Mostrar inativas</Label>
            </div>
          </div>

          {isLoading ? (
            <p className="text-sm text-muted-foreground">Carregando…</p>
          ) : filtered.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-8 text-center space-y-2">
                <ShieldCheck className="w-8 h-8 mx-auto text-muted-foreground" />
                <p className="text-sm font-medium">Nenhuma entidade cadastrada ainda</p>
                <p className="text-xs text-muted-foreground max-w-md mx-auto">
                  Entidades verificadas são a fonte autoritativa de fatos sobre pessoas, clínicas, produtos ou lugares do seu grupo. Quando você cadastra aqui, Syra usa esses dados com prioridade máxima, mesmo contra o que ela "aprendeu" no treinamento.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Identificador</TableHead>
                    <TableHead>Aliases</TableHead>
                    <TableHead>Escopo</TableHead>
                    <TableHead>Verificado</TableHead>
                    <TableHead>Confiança</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[100px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((e) => {
                    const groupName = e.group_id
                      ? groups.find((g) => g.id === e.group_id)?.name ?? "—"
                      : "Org-wide";
                    const aliasesShort = (e.aliases ?? []).slice(0, 3);
                    const extra = (e.aliases?.length ?? 0) - aliasesShort.length;
                    return (
                      <TableRow key={e.id} className="cursor-pointer" onClick={() => openEdit(e)}>
                        <TableCell className="font-medium">{e.primary_name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {ENTITY_TYPES.find((t) => t.value === e.entity_type)?.label ?? e.entity_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {e.identifier_type && e.identifier_value
                            ? `${e.identifier_type}: ${e.identifier_value}` : "—"}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {aliasesShort.map((a) => (
                              <Badge key={a} variant="secondary" className="text-xs">{a}</Badge>
                            ))}
                            {extra > 0 && <Badge variant="secondary" className="text-xs">+{extra}</Badge>}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs">{groupName}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(e.verified_at), { locale: ptBR, addSuffix: true })}
                        </TableCell>
                        <TableCell className="text-xs">{Number(e.confidence).toFixed(2)}</TableCell>
                        <TableCell>
                          <Badge variant={e.active ? "default" : "secondary"}>
                            {e.active ? "Ativa" : "Inativa"}
                          </Badge>
                        </TableCell>
                        <TableCell onClick={(ev) => ev.stopPropagation()}>
                          {canManage && (
                            <div className="flex gap-1">
                              <Button size="sm" variant="ghost" onClick={() => openEdit(e)}>
                                <Pencil className="w-4 h-4" />
                              </Button>
                              {e.active && (
                                <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(e)}>
                                  <Trash2 className="w-4 h-4 text-destructive" />
                                </Button>
                              )}
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent className="sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{form.id ? "Editar entidade" : "Nova entidade verificada"}</SheetTitle>
          </SheetHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tipo</Label>
                <Select
                  value={form.entity_type}
                  onValueChange={(v) => setForm({ ...form, entity_type: v })}
                  disabled={!canManage}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ENTITY_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Escopo</Label>
                <Select
                  value={form.group_id}
                  onValueChange={(v) => setForm({ ...form, group_id: v })}
                  disabled={!canManage}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__org__">Org inteira</SelectItem>
                    {groups.map((g) => (
                      <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Nome principal *</Label>
              <Input
                value={form.primary_name}
                onChange={(e) => setForm({ ...form, primary_name: e.target.value })}
                disabled={!canManage}
              />
            </div>

            <div>
              <Label>Aliases (separados por vírgula)</Label>
              <Input
                value={form.aliases}
                onChange={(e) => setForm({ ...form, aliases: e.target.value })}
                placeholder="Ex: Dr. Shimba, Leandro"
                disabled={!canManage}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tipo de identificador</Label>
                <Input
                  value={form.identifier_type}
                  onChange={(e) => setForm({ ...form, identifier_type: e.target.value })}
                  placeholder="CRM, CNPJ, CPF…"
                  disabled={!canManage}
                />
              </div>
              <div>
                <Label>Valor do identificador</Label>
                <Input
                  value={form.identifier_value}
                  onChange={(e) => setForm({ ...form, identifier_value: e.target.value })}
                  placeholder="Ex: 13879-SP"
                  disabled={!canManage}
                />
              </div>
            </div>

            <div>
              <Label>Resumo * <span className="text-xs text-muted-foreground">({form.summary.length}/300)</span></Label>
              <Textarea
                value={form.summary}
                onChange={(e) => setForm({ ...form, summary: e.target.value.slice(0, 300) })}
                rows={3}
                disabled={!canManage}
                placeholder="1-2 frases factuais que a IA pode ler e usar."
              />
            </div>

            <div>
              <Label>Profile (JSON estruturado)</Label>
              <Textarea
                value={form.profile_json}
                onChange={(e) => setForm({ ...form, profile_json: e.target.value })}
                rows={5}
                className="font-mono text-xs"
                disabled={!canManage}
              />
            </div>

            <div>
              <Label>Source URL</Label>
              <Input
                value={form.source_url}
                onChange={(e) => setForm({ ...form, source_url: e.target.value })}
                disabled={!canManage}
              />
            </div>

            <div>
              <Label>Notas de verificação</Label>
              <Textarea
                value={form.source_notes}
                onChange={(e) => setForm({ ...form, source_notes: e.target.value })}
                rows={2}
                disabled={!canManage}
              />
            </div>

            <div>
              <Label>Confiança: {form.confidence.toFixed(2)}</Label>
              <Slider
                value={[form.confidence]}
                onValueChange={([v]) => setForm({ ...form, confidence: v })}
                min={0.5} max={1} step={0.05}
                disabled={!canManage}
              />
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id="ve-active"
                checked={form.active}
                onCheckedChange={(v) => setForm({ ...form, active: v })}
                disabled={!canManage}
              />
              <Label htmlFor="ve-active">Ativa</Label>
            </div>
          </div>
          <SheetFooter className="mt-6 gap-2">
            <Button variant="outline" onClick={() => setDrawerOpen(false)}>Cancelar</Button>
            {canManage && (
              <Button
                onClick={handleSave}
                disabled={!form.primary_name.trim() || !form.summary.trim()}
              >
                Salvar
              </Button>
            )}
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desativar entidade?</AlertDialogTitle>
            <AlertDialogDescription>
              "{confirmDelete?.primary_name}" deixará de ser injetada nas respostas da Syra.
              Você pode reativá-la depois pela lista (mostrar inativas).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleSoftDelete}>Desativar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function CortexVerifiedEntities() {
  return (
    <RoleGate permission="cortex.entities.read" fallback={<NotFound />}>
      <CortexVerifiedEntitiesInner />
    </RoleGate>
  );
}
